import type { AgentInteractionState, OrchestratorState } from '@demo-portal/shared';
import React from 'react';

interface OrchestrationStatusBarProps {
  orchestratorState: OrchestratorState;
  agentInteractions: Map<string, AgentInteractionState>;
  isProcessing: boolean;
  primaryColor?: string;
}

const OrchestrationStatusBar: React.FC<OrchestrationStatusBarProps> = ({
  orchestratorState,
  agentInteractions,
  isProcessing,
  primaryColor = '#3498db',
}) => {
  const getStatusColor = () => {
    switch (orchestratorState.status) {
      case 'initializing': return '#f39c12';
      case 'discovering': return '#e67e22';
      case 'ready': return '#27ae60';
      case 'processing': return '#3498db';
      case 'synthesizing': return '#9b59b6';
      default: return '#95a5a6';
    }
  };

  const getStatusIcon = () => {
    switch (orchestratorState.status) {
      case 'initializing': return '⚡';
      case 'discovering': return '🔍';
      case 'ready': return '✅';
      case 'processing': return '⚙️';
      case 'synthesizing': return '🧠';
      default: return '💤';
    }
  };

  const activeAgentCount = Array.from(agentInteractions.values())
    .filter(interaction => interaction.status !== 'idle' && interaction.status !== 'complete').length;

  const completedAgentCount = Array.from(agentInteractions.values())
    .filter(interaction => interaction.status === 'complete').length;

  const totalAgents = agentInteractions.size;

  return (
    <div style={{
      position: 'sticky',
      top: 0,
      background: 'white',
      borderBottom: '1px solid #dee2e6',
      padding: '10px 20px',
      zIndex: 100,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        maxWidth: '1600px',
        margin: '0 auto',
      }}>
        {/* Status Section */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: `${getStatusColor()}20`,
            padding: '6px 12px',
            borderRadius: '20px',
            border: `1px solid ${getStatusColor()}40`,
          }}>
            <span style={{ 
              fontSize: '1.1em',
              animation: isProcessing ? 'spin 2s linear infinite' : 'none',
            }}>
              {getStatusIcon()}
            </span>
            <span style={{
              fontWeight: 'bold',
              color: getStatusColor(),
              fontSize: '0.9em',
              textTransform: 'capitalize',
            }}>
              {orchestratorState.status.replace('_', ' ')}
            </span>
          </div>

          {/* Agent Progress */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.85em',
            color: '#666',
          }}>
            <span>🤖</span>
            <span>
              <strong>{activeAgentCount}</strong> active, 
              <strong> {completedAgentCount}</strong> complete
              {totalAgents > 0 && <span> of <strong>{totalAgents}</strong></span>}
            </span>
          </div>
        </div>

        {/* Current Step and Queue Info */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '0.85em',
          flex: 1,
          justifyContent: 'center',
        }}>
          {orchestratorState.currentStep && (
            <>
              <div style={{
                background: `${primaryColor}20`,
                padding: '4px 8px',
                borderRadius: '12px',
                border: `1px solid ${primaryColor}40`,
                color: primaryColor,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <span style={{ fontSize: '1.1em' }}>
                  {orchestratorState.currentStep.action === 'analyze' ? '🧠' :
                   orchestratorState.currentStep.action === 'call_agent' ? '📞' :
                   orchestratorState.currentStep.action === 'synthesize' ? '⚡' : '⚙️'}
                </span>
                {orchestratorState.currentStep.action.replace('_', ' ')}
              </div>
              <div style={{ 
                color: '#666', 
                maxWidth: '300px', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                fontWeight: '500',
              }}>
                {orchestratorState.currentStep.description}
              </div>
            </>
          )}

          {/* Processing Queue with Enhanced Info */}
          {orchestratorState.processingQueue.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffeaa7',
                padding: '4px 8px',
                borderRadius: '12px',
                color: '#856404',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <span style={{ fontSize: '1em' }}>⏳</span>
                {orchestratorState.processingQueue.length} queued
              </div>
              {orchestratorState.processingQueue.length > 0 && (
                <div style={{
                  color: '#666',
                  fontSize: '0.8em',
                  maxWidth: '150px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  Next: {orchestratorState.processingQueue[0]?.description}
                </div>
              )}
            </div>
          )}

          {/* Idle State */}
          {!orchestratorState.currentStep && orchestratorState.processingQueue.length === 0 && orchestratorState.status === 'ready' && (
            <div style={{
              color: '#666',
              fontStyle: 'italic',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <span style={{ fontSize: '1.1em' }}>💤</span>
              Ready for next request
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {isProcessing && totalAgents > 0 && (
          <div style={{
            width: '120px',
            height: '6px',
            background: '#e9ecef',
            borderRadius: '3px',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${(completedAgentCount / totalAgents) * 100}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}aa)`,
              borderRadius: '3px',
              transition: 'width 0.5s ease',
            }} />
          </div>
        )}
      </div>

      {/* CSS for animations */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default OrchestrationStatusBar;